import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import { useInternalAdmin } from 'hooks'
import audienceApi from 'services/api/audience'
import llmPlatformApi from '../../services/api/llmPlatform'
import {
  assistConversationStorageKey,
  ASSIST_HELP_MESSAGE,
  clearAssistConversationStorage,
  findTopicForDeepLink,
  isConfirmCommand,
  isDeclineCommand,
  isHelpCommand,
  isNewConversationCommand,
  isSameAssistPath,
  NEW_CONVERSATION_WELCOME_MESSAGE,
  persistAssistConversationId,
  readAssistConversationId
} from './assistConversation'
import { getAssistTopicsForPath } from './assistTopics'
import AssistMessageContent from './AssistMessageContent'
import assistIcon from './assistIcon.svg'
import styles from './DirectfulAssist.module.scss'

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 12l16-7-7 16-2-5-7-4z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
)

const DirectfulAssist = () => {
  const location = useLocation()
  const isInternalAdmin = useInternalAdmin()
  const currentHotelId = useSelector(s => s.login.currentHotelId)
  const subsidiaryList = useSelector(s => s.login.subsidiaryList)
  const rootRef = useRef()
  const messagesEndRef = useRef()
  const taskIdHandled = useRef(false)

  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [conversationId, setConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pendingConfirm, setPendingConfirm] = useState(null)
  const [audiences, setAudiences] = useState([])

  const currentSubsidiary = subsidiaryList?.find(
    s => s.subsidiaryId === currentHotelId || s.id === currentHotelId
  )
  const assistTenantId =
    currentHotelId || currentSubsidiary?.id || currentSubsidiary?.subsidiaryId
  const { screen, topics } = getAssistTopicsForPath(location.pathname)

  const getTenantStorageIds = useCallback(
    () =>
      [assistTenantId, currentSubsidiary?.subsidiaryId, currentSubsidiary?.id].filter(Boolean),
    [assistTenantId, currentSubsidiary]
  )

  const loadTasks = useCallback(async () => {
    if (!assistTenantId) return

    try {
      const result = await llmPlatformApi.getAssistTasks(assistTenantId, 'open')
      setTasks(result?.tasks || [])
    } catch (e) {
      // Task inbox is optional — don't block chat
    }
  }, [assistTenantId])

  const resetConversation = useCallback(
    ({ showWelcome = false } = {}) => {
      clearAssistConversationStorage(getTenantStorageIds())
      setConversationId(null)
      setPendingConfirm(null)
      setError(null)
      setMessages(
        showWelcome ? [{ role: 'assist', text: NEW_CONVERSATION_WELCOME_MESSAGE }] : []
      )
    },
    [getTenantStorageIds]
  )

  useEffect(() => {
    if (!assistTenantId) return

    try {
      const navEntry = performance.getEntriesByType('navigation')[0]
      if (navEntry?.type === 'reload') {
        sessionStorage.removeItem(assistConversationStorageKey(assistTenantId))
        setConversationId(null)
        return
      }

      const stored = sessionStorage.getItem(assistConversationStorageKey(assistTenantId))
      if (stored) {
        setConversationId(stored)
      }
    } catch (e) {
      // sessionStorage unavailable — Assist still works without cross-page continuity
    }
  }, [assistTenantId])

  useEffect(() => {
    if (isOpen && assistTenantId) {
      loadTasks()
    }
  }, [isOpen, assistTenantId, loadTasks])

  useEffect(() => {
    if (location.pathname === '/directful-assist') {
      setIsOpen(true)
    }
  }, [location.pathname])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const taskId = params.get('taskId')
    if (!taskId || !assistTenantId || taskIdHandled.current) {
      return
    }

    taskIdHandled.current = true
    setIsOpen(true)

    llmPlatformApi
      .getAssistTasks(assistTenantId, 'open')
      .then(result => {
        const match = (result?.tasks || []).find(t => t.taskId === taskId)
        if (!match) {
          return
        }

        const taskMessage =
          match.message || match.payload?.message || match.payload?.card?.body
        if (taskMessage) {
          setMessages([{ role: 'assist', text: taskMessage }])
        }
      })
      .catch(() => {
        // Deep link task load is optional — chat still works without it.
      })
  }, [location.search, assistTenantId])

  useEffect(() => {
    if (!assistTenantId) return

    audienceApi
      .getList()
      .then(audienceList => {
        setAudiences((audienceList || []).filter(a => a && !a.isDeleted))
      })
      .catch(() => {
        // Audiences are optional for Assist chat — drip launch needs them when available.
      })
  }, [assistTenantId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading, isOpen])

  useEffect(() => {
    const handlePointerDown = event => {
      if (!isOpen || rootRef.current?.contains(event.target)) {
        return
      }

      setIsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isOpen])

  const appendMessage = (role, text, meta = {}) => {
    setMessages(prev => [...prev, { role, text, ...meta }])
  }

  const openCrispChat = (prefill = 'Hi — I need help from Directful Assist.') => {
    if (!window.$crisp) return
    try {
      window.$crisp.push(['do', 'chat:open'])
      window.$crisp.push(['set', 'message:text', [prefill]])
    } catch (e) {
      console.warn(e)
    }
  }

  const handleCardAction = action => {
    if (!action) return
    if (action.type === 'open_crisp') {
      openCrispChat(action.prefill)
      return
    }
    if (action.type === 'mailto' && action.href) {
      window.location.href = action.href
    }
  }

  const handleDeepLinkActivate = link => {
    const topic = findTopicForDeepLink(link, topics)
    if (topic?.message) {
      handleSend(topic.message)
      return
    }

    appendMessage(
      'assist',
      `You are already on ${screen}. Pick a suggestion below or ask a new question.`
    )
  }

  const handleSend = async textOverride => {
    const userText = (textOverride ?? message).trim()
    if (!userText || !assistTenantId || loading) return

    setMessage('')

    if (isHelpCommand(userText)) {
      appendMessage('user', userText)
      setPendingConfirm(null)
      clearAssistConversationStorage(getTenantStorageIds())
      setConversationId(null)
      setError(null)
      appendMessage('assist', ASSIST_HELP_MESSAGE)
      return
    }

    if (isNewConversationCommand(userText)) {
      resetConversation({ showWelcome: true })
      return
    }

    if (pendingConfirm) {
      if (isConfirmCommand(userText)) {
        appendMessage('user', userText)
        await handleConfirm()
        return
      }

      if (isDeclineCommand(userText)) {
        appendMessage('user', userText)
        await handleDecline()
        return
      }

      setPendingConfirm(null)
      clearAssistConversationStorage(getTenantStorageIds())
      setConversationId(null)
    }

    appendMessage('user', userText)
    setLoading(true)
    setError(null)

    try {
      const audiencePayload = audiences.map(a => ({
        id: a.id,
        name: a.name,
        size: a.size
      }))

      const effectiveConversationId =
        conversationId || readAssistConversationId(getTenantStorageIds())

      const result = await llmPlatformApi.assistRespond(
        assistTenantId,
        null,
        userText,
        effectiveConversationId,
        'dashboard',
        audiencePayload
      )

      if (!result) {
        throw new Error('Assist request returned an empty response')
      }

      if (result?.sessionId) {
        setConversationId(result.sessionId)
        persistAssistConversationId(getTenantStorageIds(), result.sessionId)
      }

      appendMessage('assist', result?.message || 'Assist did not return a message.', {
        taskId: result?.taskId,
        card: result?.card,
        state: result?.state
      })

      const followUpMessages = Array.isArray(result?.additionalMessages)
        ? result.additionalMessages.filter(item => (item || '').trim())
        : []
      followUpMessages.forEach(extra => {
        appendMessage('assist', extra)
      })

      if (result?.requiresConfirmation && result?.confirmationToken) {
        setPendingConfirm({
          taskId: result.taskId,
          token: result.confirmationToken,
          preview: result.message
        })
      } else {
        setPendingConfirm(null)
      }

      loadTasks()
    } catch (e) {
      setError(e.message || 'Assist request failed')
    } finally {
      setLoading(false)
    }
  }

  const handleTopicClick = topic => {
    if (!assistTenantId || loading) return
    handleSend(topic.message)
  }

  const handleConfirm = async () => {
    if (!pendingConfirm || !assistTenantId) return
    setLoading(true)
    try {
      const effectiveConversationId =
        conversationId || readAssistConversationId(getTenantStorageIds())
      const result = await llmPlatformApi.confirmAssistTask(
        pendingConfirm.taskId,
        pendingConfirm.token,
        assistTenantId,
        'dashboard',
        effectiveConversationId
      )
      appendMessage('assist', result.message || 'Action confirmed.')
      setPendingConfirm(null)
      loadTasks()
    } catch (e) {
      setError(e.message || 'Confirmation failed')
    } finally {
      setLoading(false)
    }
  }

  const handleDecline = async () => {
    if (!pendingConfirm || !assistTenantId) return
    setLoading(true)
    try {
      const effectiveConversationId =
        conversationId || readAssistConversationId(getTenantStorageIds())
      const result = await llmPlatformApi.declineAssistTask(
        pendingConfirm.taskId,
        assistTenantId,
        effectiveConversationId,
        pendingConfirm.token
      )
      appendMessage('assist', result.message || 'Action declined.')
      setPendingConfirm(null)
      loadTasks()
    } catch (e) {
      setError(e.message || 'Decline failed')
    } finally {
      setLoading(false)
    }
  }

  const handleAskSomethingElse = () => {
    setPendingConfirm(null)
    clearAssistConversationStorage(getTenantStorageIds())
    setConversationId(null)
    appendMessage('assist', NEW_CONVERSATION_WELCOME_MESSAGE)
  }

  const renderTopicSuggestions = compact => (
    <>
      <p className={compact ? styles.topicsBarLabel : styles.screenLabel}>
        Suggested for {screen}
      </p>
      <div className={styles.topics}>
        {topics.map(topic => (
          <button
            key={topic.label}
            type="button"
            className={compact ? styles.topicChipCompact : styles.topicChip}
            onClick={() => handleTopicClick(topic)}
            disabled={loading || !assistTenantId}
          >
            {topic.label}
          </button>
        ))}
      </div>
    </>
  )

  if (!isInternalAdmin) {
    return null
  }

  return (
    <div className={styles.root} ref={rootRef}>
      {isOpen && (
        <div className={styles.panel} role="dialog" aria-label="Directful Assist">
          <div className={styles.header}>
            <div className={styles.avatar}>
              <img src={assistIcon} alt="" className={styles.avatarImg} />
            </div>
            <div className={styles.headerText}>
              <h2>Directful Assist</h2>
              <p>Revenue copilot · {screen}</p>
            </div>
            {(messages.length > 0 || pendingConfirm) && (
              <button
                type="button"
                className={styles.newConversationBtn}
                onClick={() => resetConversation({ showWelcome: true })}
                disabled={loading}
              >
                New chat
              </button>
            )}
          </div>

          {!assistTenantId && (
            <div className={styles.notice}>Select a hotel to use Directful Assist.</div>
          )}

          {tasks.length > 0 && (
            <div className={styles.inbox}>
              <h3>Open tasks ({tasks.length})</h3>
              {tasks.map(task => (
                <div key={task.taskId} className={styles.taskCard}>
                  <strong>{task.type?.replace(/_/g, ' ')}</strong>
                  <span>{task.state}</span>
                  {task.origin === 'system' && task.opportunityScore != null && (
                    <span className={styles.score}>Score: {task.opportunityScore.toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className={styles.body}>
            {messages.length === 0 && (
              <div className={styles.welcome}>
                <p>Ask a question or describe what you need help with.</p>
                {topics.length > 0 && assistTenantId && renderTopicSuggestions(false)}
              </div>
            )}

            <div className={styles.messages}>
              {messages.map((msg, idx) => (
                <div key={idx} className={msg.role === 'user' ? styles.userMsg : styles.assistMsg}>
                  {msg.role === 'user' ? (
                    <p>{msg.text}</p>
                  ) : (
                    <AssistMessageContent text={msg.text} />
                  )}
                  {msg.card?.deepLinks?.map(link =>
                    isSameAssistPath(link.path, location.pathname) ? (
                      <button
                        key={`${link.path}-${link.label}`}
                        type="button"
                        className={styles.deepLinkButton}
                        onClick={() => handleDeepLinkActivate(link)}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link key={link.path} to={link.path} className={styles.deepLink}>
                        {link.label}
                      </Link>
                    )
                  )}
                  {msg.card?.actions?.map(action => (
                    <button
                      key={`${action.type}-${action.label}`}
                      type="button"
                      className={styles.cardAction}
                      onClick={() => handleCardAction(action)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              ))}
              {loading && (
                <div className={styles.typing} aria-label="Assist is thinking">
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {messages.length > 0 && topics.length > 0 && assistTenantId && (
              <div className={styles.topicsBar}>{renderTopicSuggestions(true)}</div>
            )}
          </div>

          {pendingConfirm && (
            <div className={styles.confirmBar}>
              <AssistMessageContent text={pendingConfirm.preview} />
              <div className={styles.confirmActions}>
                <button type="button" onClick={handleConfirm} disabled={loading}>
                  Confirm
                </button>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={handleDecline}
                  disabled={loading}
                >
                  Decline
                </button>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={handleAskSomethingElse}
                  disabled={loading}
                >
                  Ask something else
                </button>
              </div>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.composer}>
            <div className={styles.inputWrap}>
              <input
                type="text"
                value={message}
                placeholder="Write a message..."
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                disabled={loading || !assistTenantId}
              />
            </div>
            <button
              type="button"
              className={styles.sendBtn}
              onClick={() => handleSend()}
              disabled={loading || !assistTenantId || !message.trim()}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`${styles.launcher} ${isOpen ? styles.launcherOpen : ''}`}
        onClick={() => setIsOpen(open => !open)}
        title={isOpen ? 'Close Directful Assist' : 'Directful Assist'}
        aria-label={isOpen ? 'Close Directful Assist' : 'Open Directful Assist'}
        aria-expanded={isOpen}
      >
        {isOpen ? <CloseIcon /> : <img src={assistIcon} alt="" className={styles.launcherImg} />}
      </button>
    </div>
  )
}

export default DirectfulAssist
